import { useState, useEffect, useCallback } from "react";
import { formatDate } from "@/lib/formatDate";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Badge } from "@repo/ui/badge";
import { Separator } from "@repo/ui/separator";
import { Switch } from "@repo/ui/switch";
import { Users, Shield, Key, Bell, Save, UserPlus, Trash2, Loader2, AlertCircle, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/alert-dialog";
import { Alert, AlertDescription } from "@repo/ui/alert";
import { HelpTooltip } from "@/components/common/HelpTooltip";
import { CrawlPolicySettingsCard } from "@/components/settings/CrawlPolicySettingsCard";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchExportRuntimeResolution,
  fetchExportRuntimeSettings,
  fetchProviderSettings,
  upsertAppSetting,
  upsertProviderSettings,
} from "@/lib/api";
import type { RuntimeSettingSource } from "@/lib/api";

type UserRole = "admin" | "operator" | "viewer";

interface UserData {
  id: string;
  email: string;
  role: UserRole | null;
  fullName: string | null;
  lastSignIn: string | null;
  createdAt: string;
  pending?: boolean;
  invitedAt?: string | null;
}

interface GlobalProviderSettingsForm {
  use_auto: boolean;
  enabled_providers: string[];
  provider_preference: string[];
  retry_count: number;
  timeout_ms: number;
}

interface ExportRuntimeSettingsForm {
  apiPageSize: number;
  chunkSizeRows: number;
  maxRows: number;
  defaultColumnsText: string;
}

const runtimeSourceClass: Record<RuntimeSettingSource["source"], string> = {
  env: "bg-primary/15 text-primary",
  db: "bg-accent/15 text-accent",
  default: "bg-muted text-muted-foreground",
};


const roleConfig: Record<UserRole, { label: string; description: string; className: string }> = {
  admin: { label: "Admin", description: "Full access to all features", className: "bg-primary/15 text-primary" },
  operator: { label: "Operator", description: "Can run and manage jobs", className: "bg-accent/15 text-accent" },
  viewer: { label: "Viewer", description: "Read-only access", className: "bg-muted text-muted-foreground" },
};

const Settings = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [failureAlerts, setFailureAlerts] = useState(true);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>("viewer");
  const [isInviting, setIsInviting] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null);
  const [needsAdminSetup, setNeedsAdminSetup] = useState(false);
  const [isSettingUpAdmin, setIsSettingUpAdmin] = useState(false);
  const [providerSettings, setProviderSettings] = useState<GlobalProviderSettingsForm>({
    use_auto: true,
    enabled_providers: ["firecrawl_scrape", "zyte", "direct"],
    provider_preference: ["firecrawl_scrape", "zyte", "direct"],
    retry_count: 2,
    timeout_ms: 45000,
  });
  const [isSavingProviders, setIsSavingProviders] = useState(false);

  const [exportRuntimeSources, setExportRuntimeSources] = useState<RuntimeSettingSource[]>([]);
  const [exportRuntimeForm, setExportRuntimeForm] = useState<ExportRuntimeSettingsForm>({
    apiPageSize: 1000,
    chunkSizeRows: 10000,
    maxRows: 1000000,
    defaultColumnsText: "",
  });
  const [isSavingExportRuntime, setIsSavingExportRuntime] = useState(false);

  const { toast } = useToast();
  const { user, role, isAdmin, refreshRole } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoadingUsers(true);
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "list" },
      });

      if (error) {
        console.error("Error fetching users:", error);
        // Check if it's a permission error
        if (error.message?.includes("403") || error.message?.includes("Forbidden")) {
          setNeedsAdminSetup(true);
        }
        return;
      }

      if (data?.users) {
        setUsers(data.users);
        setNeedsAdminSetup(false);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const handleMakeAdmin = async () => {
    try {
      setIsSettingUpAdmin(true);
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "make-admin" },
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to set up admin access",
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: "Error",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "You are now an admin. Refreshing...",
      });

      await refreshRole();
      setNeedsAdminSetup(false);
      fetchUsers();
    } catch (err) {
      console.error("Error setting up admin:", err);
      toast({
        title: "Error",
        description: "Failed to set up admin access",
        variant: "destructive",
      });
    } finally {
      setIsSettingUpAdmin(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsInviting(true);
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: {
          action: "invite",
          email: inviteEmail.trim(),
          role: inviteRole,
        },
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to invite user",
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: "Error",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: data.message || "User invited successfully",
      });

      setInviteEmail("");
      setInviteRole("viewer");
      setIsInviteOpen(false);
      fetchUsers();
    } catch (err) {
      console.error("Error inviting user:", err);
      toast({
        title: "Error",
        description: "Failed to invite user",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: UserRole) => {
    try {
      setUpdatingRoleId(userId);
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: {
          action: "update-role",
          userId,
          role: newRole,
        },
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to update role",
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: "Error",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Role updated successfully",
      });

      fetchUsers();
    } catch (err) {
      console.error("Error updating role:", err);
      toast({
        title: "Error",
        description: "Failed to update role",
        variant: "destructive",
      });
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;

    try {
      setIsDeleting(true);
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: {
          action: "delete",
          userId: deleteUserId,
        },
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to delete user",
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: "Error",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "User deleted successfully",
      });

      fetchUsers();
    } catch (err) {
      console.error("Error deleting user:", err);
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteUserId(null);
    }
  };

  useEffect(() => {
    // Any signed-in user can call list-users — the edge function redacts
    // PII (last sign-in) for non-admins. This lets the team see who's
    // on the platform without requiring admin role.
    if (user) {
      fetchUsers();
    }
  }, [user, fetchUsers]);

  // Admin-bootstrap gating. The button is meaningful ONLY when the
  // system has zero admins — once anyone is admin, the server-side
  // make-admin handler rejects with 403, so the UI shouldn't tempt
  // users with a guaranteed-to-fail button. Previously the button
  // appeared for every signed-in user with role === null (including
  // freshly invited operators/viewers).
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user || role) {
        if (!cancelled) {
          setNeedsAdminSetup(false);
          setIsLoadingUsers(false);
        }
        return;
      }
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "has-any-admin" },
      });
      if (cancelled) return;
      if (error) {
        // Failsafe: don't show the button if we can't confirm — better
        // than letting a brand new user wrongly claim admin.
        setNeedsAdminSetup(false);
        setIsLoadingUsers(false);
        return;
      }
      const hasAdmin = Boolean(data?.hasAdmin);
      setNeedsAdminSetup(!hasAdmin);
      setIsLoadingUsers(false);
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [user, role]);

  useEffect(() => {
    const loadProviderSettings = async () => {
      const detail = await fetchProviderSettings("detail");
      if (!detail) return;
      setProviderSettings({
        use_auto: detail.use_auto,
        enabled_providers: detail.enabled_providers,
        provider_preference: detail.provider_preference,
        retry_count: detail.retry_count,
        timeout_ms: detail.timeout_ms,
      });
    };
    loadProviderSettings();
  }, []);

  const toggleProvider = (provider: string) => {
    setProviderSettings((prev) => {
      const nextEnabled = prev.enabled_providers.includes(provider)
        ? prev.enabled_providers.filter((item) => item !== provider)
        : [...prev.enabled_providers, provider];
      const nextPreference = prev.provider_preference.includes(provider)
        ? prev.provider_preference
        : [...prev.provider_preference, provider];
      return {
        ...prev,
        enabled_providers: nextEnabled.length > 0 ? nextEnabled : ["firecrawl_scrape"],
        provider_preference: nextPreference,
      };
    });
  };

  const toggleProviderPreference = (provider: string) => {
    setProviderSettings((prev) => {
      const next = prev.provider_preference.includes(provider)
        ? prev.provider_preference.filter((item) => item !== provider)
        : [...prev.provider_preference, provider];
      return {
        ...prev,
        provider_preference: next.length > 0 ? next : ["firecrawl_scrape"],
      };
    });
  };

  const loadExportRuntime = useCallback(async () => {
    const resolution = await fetchExportRuntimeResolution();
    const effective = resolution?.effective ?? (await fetchExportRuntimeSettings());
    setExportRuntimeSources(resolution?.sources ?? []);
    setExportRuntimeForm({
      apiPageSize: effective.apiPageSize,
      chunkSizeRows: effective.chunkSizeRows,
      maxRows: effective.maxRows,
      defaultColumnsText: effective.defaultColumns.join(",\n"),
    });
  }, []);

  useEffect(() => {
    loadExportRuntime();
  }, [loadExportRuntime]);

  const handleSaveExportRuntime = async () => {
    try {
      setIsSavingExportRuntime(true);
      const parsedColumns = exportRuntimeForm.defaultColumnsText
        .split(/[,\n]/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
      const writes = await Promise.all([
        upsertAppSetting({
          key: "export.api_page_size",
          value: Math.max(100, exportRuntimeForm.apiPageSize),
          description: "Rows fetched per DB page during export.",
        }),
        upsertAppSetting({
          key: "export.chunk_size_rows",
          value: Math.max(1000, exportRuntimeForm.chunkSizeRows),
          description: "Rows per chunk file.",
        }),
        upsertAppSetting({
          key: "export.max_rows",
          value: Math.max(1000, exportRuntimeForm.maxRows),
          description: "Hard ceiling rows per export.",
        }),
        upsertAppSetting({
          key: "export.default_columns",
          value: parsedColumns,
          description: "Default selected columns for exports.",
        }),
      ]);
      if (writes.some((result) => !result)) {
        throw new Error("Failed to save one or more runtime settings");
      }
      toast({
        title: "Export defaults saved",
        description: "Runtime defaults updated (env overrides still win).",
      });
      await loadExportRuntime();
    } catch (error) {
      console.error("Failed to save export runtime settings:", error);
      toast({
        title: "Save failed",
        description: "Could not save export runtime defaults",
        variant: "destructive",
      });
    } finally {
      setIsSavingExportRuntime(false);
    }
  };

  const handleSaveProviderSettings = async () => {
    try {
      setIsSavingProviders(true);
      const result = await upsertProviderSettings({
        scope: "global",
        operation_type: "detail",
        use_auto: providerSettings.use_auto,
        enabled_providers: providerSettings.enabled_providers,
        provider_preference: providerSettings.provider_preference,
        retry_count: providerSettings.retry_count,
        timeout_ms: providerSettings.timeout_ms,
        metadata: {},
      });
      if (!result) {
        throw new Error("Failed to save provider settings");
      }
      toast({
        title: "Provider settings saved",
        description: "Global scrape provider defaults updated.",
      });
    } catch (error) {
      console.error("Error saving provider settings:", error);
      toast({
        title: "Error",
        description: "Failed to save provider settings",
        variant: "destructive",
      });
    } finally {
      setIsSavingProviders(false);
    }
  };

  // (admin-setup check moved to the system-wide has-any-admin effect above)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage users, permissions, and system configuration
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="api" className="gap-2">
            <Key className="w-4 h-4" />
            API
          </TabsTrigger>
          <TabsTrigger value="exports" className="gap-2">
            <Download className="w-4 h-4" />
            Exports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          {needsAdminSetup && (
            <Alert className="border-accent">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  No admin configured yet. Set yourself as the first admin to manage users.
                </span>
                <Button
                  size="sm"
                  onClick={handleMakeAdmin}
                  disabled={isSettingUpAdmin}
                >
                  {isSettingUpAdmin ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  Make Me Admin
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <div className="card-elevated">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Team Members</h3>
                <p className="text-sm text-muted-foreground">
                  Manage who has access to this dashboard
                </p>
              </div>
              {isAdmin && (
                <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Invite Team Member</DialogTitle>
                      <DialogDescription>
                        Send an invitation email to add a new team member.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="invite-email">Email Address</Label>
                        <Input
                          id="invite-email"
                          type="email"
                          placeholder="colleague@equitystart.com"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invite-role">Role</Label>
                        <Select
                          value={inviteRole}
                          onValueChange={(v) => setInviteRole(v as UserRole)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="operator">Operator</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {roleConfig[inviteRole].description}
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsInviteOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleInviteUser} disabled={isInviting}>
                        {isInviting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Send Invitation
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {isLoadingUsers && !needsAdminSetup ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : users.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role / Status</TableHead>
                    {isAdmin && <TableHead>Last Login</TableHead>}
                    <TableHead>Joined</TableHead>
                    {isAdmin && <TableHead className="w-32"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((userData) => {
                    const userRole = userData.role ? roleConfig[userData.role] : null;
                    const isCurrentUser = userData.id === user?.id;
                    const isPending = userData.pending === true;

                    return (
                      <TableRow key={userData.id}>
                        <TableCell className="font-medium">
                          {userData.email}
                          {isCurrentUser && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              You
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isPending ? (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
                              Pending invite
                            </Badge>
                          ) : userRole ? (
                            <Badge className={userRole.className}>
                              {userRole.label}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              No role
                            </Badge>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-muted-foreground">
                            {formatDate(userData.lastSignIn, { fallback: "Never" })}
                          </TableCell>
                        )}
                        <TableCell className="text-muted-foreground">
                          {formatDate(
                            userData.invitedAt ?? userData.createdAt,
                            { fallback: "—" },
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {!isPending && (
                                <Select
                                  value={userData.role || ""}
                                  onValueChange={(v) =>
                                    handleUpdateRole(userData.id, v as UserRole)
                                  }
                                  disabled={
                                    updatingRoleId === userData.id ||
                                    (isCurrentUser && userData.role === "admin")
                                  }
                                >
                                  <SelectTrigger className="h-8 w-24">
                                    {updatingRoleId === userData.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <SelectValue placeholder="Set role" />
                                    )}
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="operator">Operator</SelectItem>
                                    <SelectItem value="viewer">Viewer</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              {!isCurrentUser && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => setDeleteUserId(userData.id)}
                                  title={isPending ? "Cancel invite" : "Delete user"}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : null}
          </div>

          <div className="card-elevated p-5">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-primary" />
              <div className="space-y-2">
                <h3 className="font-semibold">Role Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  {Object.entries(roleConfig).map(([key, config]) => (
                    <div key={key} className="space-y-1">
                      <Badge className={config.className}>{config.label}</Badge>
                      <p className="text-muted-foreground">{config.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <div className="card-elevated p-5 space-y-5">
            <div>
              <h3 className="font-semibold mb-4">Notification Preferences</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email-notifications">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive email updates about job completions
                    </p>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="failure-alerts">Failure Alerts</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified immediately when a scrape job fails
                    </p>
                  </div>
                  <Switch
                    id="failure-alerts"
                    checked={failureAlerts}
                    onCheckedChange={setFailureAlerts}
                  />
                </div>
              </div>
            </div>

            <Button>
              <Save className="w-4 h-4 mr-2" />
              Save Preferences
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="api" className="space-y-4">
          <CrawlPolicySettingsCard />
          <div className="card-elevated p-5 space-y-5">
            <div>
              <h3 className="font-semibold">API Configuration</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure external API connections for scraping
              </p>

              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-accent/10 flex items-center justify-center">
                        <Key className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="font-medium">Firecrawl API</p>
                        <p className="text-sm text-muted-foreground">
                          Web scraping and crawling service
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-success/15 text-success">Connected</Badge>
                  </div>
                </div>

                <div className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-1">
                        <p className="font-medium">Global Provider Strategy (Detail Scrape)</p>
                        <HelpTooltip
                          title="Provider Strategy"
                          summary="Controls provider order and retry behavior."
                          details="Applied when adapter-level settings are not provided."
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Configure default provider mode, fallback order, retry, and timeout.
                      </p>
                    </div>
                    <Badge variant="outline">{providerSettings.use_auto ? "Auto" : "Fallback"}</Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="provider-auto">Use Auto Selection</Label>
                      <p className="text-xs text-muted-foreground">
                        Learn from historical attempts and choose best provider first.
                      </p>
                    </div>
                    <Switch
                      id="provider-auto"
                      checked={providerSettings.use_auto}
                      onCheckedChange={(checked) => setProviderSettings((prev) => ({ ...prev, use_auto: checked }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Enabled Providers</Label>
                    <div className="flex flex-wrap gap-2">
                      {["firecrawl_scrape", "firecrawl_agent", "zyte", "direct"].map((provider) => (
                        <Badge
                          key={provider}
                          variant={providerSettings.enabled_providers.includes(provider) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleProvider(provider)}
                        >
                          {provider}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Preference Order</Label>
                    <div className="flex flex-wrap gap-2">
                      {["firecrawl_scrape", "firecrawl_agent", "zyte", "direct"].map((provider) => (
                        <Badge
                          key={provider}
                          variant={providerSettings.provider_preference.includes(provider) ? "secondary" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleProviderPreference(provider)}
                        >
                          {provider}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="provider-retries">Retry Count</Label>
                      <Input
                        id="provider-retries"
                        type="number"
                        min={0}
                        max={8}
                        value={providerSettings.retry_count}
                        onChange={(event) =>
                          setProviderSettings((prev) => ({
                            ...prev,
                            retry_count: parseInt(event.target.value || "0", 10),
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="provider-timeout">Timeout (ms)</Label>
                      <Input
                        id="provider-timeout"
                        type="number"
                        min={1000}
                        step={500}
                        value={providerSettings.timeout_ms}
                        onChange={(event) =>
                          setProviderSettings((prev) => ({
                            ...prev,
                            timeout_ms: parseInt(event.target.value || "1000", 10),
                          }))
                        }
                      />
                    </div>
                  </div>

                  <Button onClick={handleSaveProviderSettings} disabled={isSavingProviders}>
                    {isSavingProviders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Provider Defaults
                  </Button>
                </div>

                <div className="p-4 border border-border rounded-lg space-y-3">
                  <h4 className="font-medium">Global Secrets & Runtime Visibility</h4>
                  <p className="text-sm text-muted-foreground">
                    API keys and secrets belong in environment variables or secret managers, never in `VITE_` vars.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge className="bg-primary/15 text-primary">1. Environment</Badge>
                    <Badge className="bg-accent/15 text-accent">2. Database (`app_settings`)</Badge>
                    <Badge variant="outline">3. Code fallback</Badge>
                  </div>
                </div>

                <div className="p-4 border border-dashed border-border rounded-lg space-y-2">
                  <h4 className="font-medium">Function Logs</h4>
                  <p className="text-sm text-muted-foreground">
                    TODO: Add unified edge-function log viewer (filter by function, level, and time range).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="exports" className="space-y-4">
          <div className="card-elevated p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-semibold">Export Runtime Defaults</h3>
              <HelpTooltip
                title="Priority"
                summary="Environment > Database > Code defaults."
                details="Default chunking, page size, and column set used when an export run does not override them."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="runtime-api-page-size">Export API Page Size</Label>
                <Input
                  id="runtime-api-page-size"
                  type="number"
                  min={100}
                  value={exportRuntimeForm.apiPageSize}
                  onChange={(event) =>
                    setExportRuntimeForm((prev) => ({
                      ...prev,
                      apiPageSize: parseInt(event.target.value || "1000", 10),
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="runtime-chunk-size">Export Chunk Rows</Label>
                <Input
                  id="runtime-chunk-size"
                  type="number"
                  min={1000}
                  value={exportRuntimeForm.chunkSizeRows}
                  onChange={(event) =>
                    setExportRuntimeForm((prev) => ({
                      ...prev,
                      chunkSizeRows: parseInt(event.target.value || "10000", 10),
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="runtime-max-rows">Export Max Rows</Label>
                <Input
                  id="runtime-max-rows"
                  type="number"
                  min={1000}
                  value={exportRuntimeForm.maxRows}
                  onChange={(event) =>
                    setExportRuntimeForm((prev) => ({
                      ...prev,
                      maxRows: parseInt(event.target.value || "1000000", 10),
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="runtime-default-columns">Default Export Columns</Label>
              <textarea
                id="runtime-default-columns"
                className="w-full min-h-28 rounded-md border bg-background px-3 py-2 text-sm"
                value={exportRuntimeForm.defaultColumnsText}
                onChange={(event) =>
                  setExportRuntimeForm((prev) => ({
                    ...prev,
                    defaultColumnsText: event.target.value,
                  }))
                }
              />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Environment variables override database values. Database values override code defaults.
              </AlertDescription>
            </Alert>

            <div className="rounded-md border p-3 space-y-2">
              <p className="text-xs font-medium">Active Runtime Sources</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                {[
                  { setting: "apiPageSize", label: "Export API Page Size" },
                  { setting: "chunkSizeRows", label: "Export Chunk Rows" },
                  { setting: "maxRows", label: "Export Max Rows" },
                  { setting: "defaultColumns", label: "Default Export Columns" },
                ].map((row) => {
                  const source = exportRuntimeSources.find((item) => item.setting === row.setting);
                  return (
                    <div key={row.setting} className="flex items-center justify-between rounded border px-2 py-1.5">
                      <span className="text-muted-foreground">{row.label}</span>
                      <div className="flex items-center gap-2">
                        {source?.value ? (
                          <span className="max-w-36 truncate text-muted-foreground">{source.value}</span>
                        ) : null}
                        <Badge
                          variant="outline"
                          className={source ? runtimeSourceClass[source.source] : "bg-muted text-muted-foreground"}
                        >
                          {source ? source.source : "unknown"}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Button type="button" onClick={handleSaveExportRuntime} disabled={isSavingExportRuntime}>
              {isSavingExportRuntime ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Export Defaults
            </Button>
          </div>
        </TabsContent>

      </Tabs>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
              The user will lose all access to the dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;